import HoverButtonSetting from 'client/components/core/Sidebar/components/Settings/components/HoverButton'
import {
  useUpdateEntity,
  useUpdatePropNew,
} from 'client/components/core/Sidebar/components/Settings/hooks/useUpdateProps'
import { ShadowStyle } from 'client/components/core/Sidebar/components/Settings/styles'
import { useDeviceModeContext } from 'client/context/DeviceModeContext'
import { usePage } from 'client/store'
import { isWebsitePageType } from 'client/store/page/pageSelectors'
import { BorderType } from 'common/types/styleTypes'
import React, { useState } from 'react'
import { GroupTitle } from 'src/editor/layout/sidebar/settings/group-title'
import { useCalculateButtonStyles } from 'src/editor/shared/hooks/use-calculate-button-styles'
import { ColorPickerControl } from 'src/editor/shared/settings/controls/color-picker/color-picker-control'
import { BorderGroup } from 'src/editor/shared/settings/groups/border-group'
import { PaddingGroup } from 'src/editor/shared/settings/groups/padding'
import { TypographyGroup } from 'src/editor/shared/settings/groups/typography-group'
import { ResetEntityStyles } from 'src/editor/shared/settings/reset-entity-styles-button'
import { Switcher } from 'src/editor/shared/ui-lib/switcher'
import { FormInterface } from 'src/shared/entities/form/form.schema'
import { ButtonCommonStylesInterface } from 'src/shared/entities/form/form-submit-button-sub-entity-common-styles.schema'
import { WebsiteSettingsTypographyTypeEnum } from 'src/shared/types/website-settings-types'

enum SettingsModeEnum {
  Style = 'style',
  Typography = 'typography',
}

interface FormButtonCommonSettingsProps {
  entity: FormInterface
  defaultEntity?: FormInterface
}

export const FormButtonCommonSettings = ({
  entity,
  defaultEntity,
}: FormButtonCommonSettingsProps) => {
  const { isMobile } = useDeviceModeContext()

  const [settingsMode, setSettingsMode] = useState<SettingsModeEnum>(
    SettingsModeEnum.Style,
  )

  const isWebsitePage = usePage(isWebsitePageType)

  const updateEntity = useUpdateEntity<FormInterface>()
  const updateProp = useUpdatePropNew(
    // NOTE: this is hack to prevent the typescript error
    entity as FormInterface & {
      buttonCommonStyles: ButtonCommonStylesInterface & {
        border: Partial<BorderType>
        mobileBorder: Partial<BorderType>
      }
    },
  )
  const updateCommonSettings = (
    buttonCommonStyles: ButtonCommonStylesInterface,
  ) => {
    updateEntity({
      ...entity,
      buttonCommonStyles,
    })
  }
  const styles = entity.buttonCommonStyles
  const firstUpdateKeys = ['buttonCommonStyles'] as const

  const {
    calculatedBorder,
    calculatedPadding,
    calculatedFontSize,
    calculatedLineHeight,
    calculatedFontFamily,
    calculatedFontWeight,
    calculatedFontStyle,
    calculatedTextColor,
    calculatedBackground,
    calculatedHover,
    globalFontFamily,
    globalMobileFontFamily,
  } = useCalculateButtonStyles({
    border: styles.border,
    mobileBorder: styles.mobileBorder,
    padding: styles.padding,
    mobilePadding: styles.mobilePadding,
    buttonType: styles.buttonType,
    fontSize: styles.textFontSize,
    mobileFontSize: styles.mobileTextFontSize,
    lineHeight: styles.lineHeight,
    mobileLineHeight: styles.mobileLineHeight,
    fontFamily: styles.textFontFamily,
    mobileFontFamily: styles.mobileTextFontFamily,
    fontWeight: styles.textFontWeight,
    mobileFontWeight: styles.mobileTextFontWeight,
    fontStyle: styles.textFontStyle,
    mobileFontStyle: styles.mobileTextFontStyle,
    textColor: styles.textColor,
    mobileTextColor: styles.mobileTextColor,
    background: styles.background,
    mobileBackground: styles.mobileBackground,
    hover: styles.hover,
    mobileHover: styles.mobileHover,
  })

  return (
    <>
      <Switcher
        wrapperClassName="mt-0 mb-8"
        btnList={[
          {
            value: SettingsModeEnum.Style,
            content: 'entity_settings.form.common_settings.style',
          },
          {
            value: SettingsModeEnum.Typography,
            content: 'entity_settings.form.common_settings.typography',
          },
        ]}
        value={settingsMode}
        update={setSettingsMode}
      />
      {settingsMode === SettingsModeEnum.Style && (
        <>
          {isWebsitePage && (
            <Switcher
              wrapperClassName="mt-0"
              labelText="settings_styles.type.label"
              btnList={[
                {
                  value: WebsiteSettingsTypographyTypeEnum.PrimaryButton,
                  content:
                    'website_styles_settings.buttons_and_links.primary.title',
                },
                {
                  value: WebsiteSettingsTypographyTypeEnum.SecondaryButton,
                  content:
                    'website_styles_settings.buttons_and_links.secondary.title',
                },
              ]}
              value={styles.buttonType}
              update={updateProp(...firstUpdateKeys, 'buttonType')}
            />
          )}
          <PaddingGroup
            isResetIconVisible
            defaultPadding={
              isMobile
                ? defaultEntity?.buttonCommonStyles.mobilePadding
                : defaultEntity?.buttonCommonStyles.padding
            }
            padding={
              isMobile ? calculatedPadding.mobile : calculatedPadding.desktop
            }
            update={updateProp(
              ...firstUpdateKeys,
              isMobile ? 'mobilePadding' : 'padding',
            )}
          />

          <BorderGroup
            isResetIconVisible
            border={
              isMobile ? calculatedBorder.mobile : calculatedBorder.desktop
            }
            defaultBorder={
              isMobile ? defaultEntity?.mobileBorder : defaultEntity?.border
            }
            update={updateProp(
              ...firstUpdateKeys,
              isMobile ? 'mobileBorder' : 'border',
            )}
            isMobile={isMobile}
            hideColorControl
          />

          <GroupTitle
            afterChildren={
              <ResetEntityStyles
                entity={entity}
                defaultEntity={defaultEntity}
                keys={[
                  [...firstUpdateKeys, 'border', 'color'],
                  [...firstUpdateKeys, 'background'],
                  [...firstUpdateKeys, 'textColor'],
                  [...firstUpdateKeys, 'subTextColor'],
                ]}
                mobileKeys={[
                  [...firstUpdateKeys, 'mobileBorder', 'color'],
                  [...firstUpdateKeys, 'mobileBackground'],
                  [...firstUpdateKeys, 'mobileTextColor'],
                  [...firstUpdateKeys, 'mobileSubTextColor'],
                ]}
              />
            }
          >
            website_styles_settings.colors.label
          </GroupTitle>
          <ColorPickerControl
            isClearable
            label="entity_settings.form.common_settings.border_color"
            color={calculatedBorder.desktop?.color}
            update={updateProp(...firstUpdateKeys, 'border', 'color')}
            mobileColor={calculatedBorder.mobile?.color}
            mobileUpdate={updateProp(
              ...firstUpdateKeys,
              'mobileBorder',
              'color',
            )}
          />
          <ColorPickerControl
            isClearable
            label="settings_styles.background_color.label"
            color={calculatedBackground.desktop}
            update={updateProp(...firstUpdateKeys, 'background')}
            mobileColor={calculatedBackground.mobile}
            mobileUpdate={updateProp(...firstUpdateKeys, 'mobileBackground')}
          />
          <ColorPickerControl
            isClearable
            label="text color"
            color={calculatedTextColor.desktop}
            update={updateProp(...firstUpdateKeys, 'textColor')}
            mobileColor={calculatedTextColor.mobile}
            mobileUpdate={updateProp(...firstUpdateKeys, 'mobileTextColor')}
          />
          <ColorPickerControl
            isClearable
            label="entity_settings.button.sub_text.color.label"
            color={styles.subTextColor}
            update={updateProp(...firstUpdateKeys, 'subTextColor')}
            mobileColor={styles.mobileSubTextColor}
            mobileUpdate={updateProp(...firstUpdateKeys, 'mobileSubTextColor')}
          />

          <ShadowStyle
            shadow={styles.boxShadow}
            mobileShadow={styles.mobileBoxShadow}
            update={updateProp(...firstUpdateKeys, 'boxShadow')}
            mobileUpdate={updateProp(...firstUpdateKeys, 'mobileBoxShadow')}
          />

          <HoverButtonSetting
            textColor={calculatedTextColor.desktop}
            backgroundColor={calculatedBackground.desktop}
            subTextColor={styles.subTextColor}
            update={updateProp(...firstUpdateKeys, 'hover')}
            mobileUpdate={updateProp(...firstUpdateKeys, 'mobileHover')}
            defaultHover={defaultEntity?.buttonCommonStyles.hover}
            mobileDefaultHover={defaultEntity?.buttonCommonStyles.mobileHover}
            hover={calculatedHover.desktop}
            mobileHover={calculatedHover.mobile}
          />
        </>
      )}
      {settingsMode === SettingsModeEnum.Typography && (
        <>
          <TypographyGroup
            withoutHorizontalLine
            hideTextAlign
            hideLetterSpacing
            entity={styles}
            updateEntity={updateCommonSettings}
            fontFamilyKey="textFontFamily"
            fontWeightKey="textFontWeight"
            fontStyleKey="textFontStyle"
            fontFileIdKey="textFontFileId"
            mobileFontFamilyKey="mobileTextFontFamily"
            mobileFontWeightKey="mobileTextFontWeight"
            mobileFontStyleKey="mobileTextFontStyle"
            mobileFontFileIdKey="mobileTextFontFileId"
            fontFamily={calculatedFontFamily.desktop}
            mobileFontFamily={calculatedFontFamily.mobile}
            fontWeight={calculatedFontWeight.desktop}
            mobileFontWeight={calculatedFontWeight.mobile}
            fontStyle={calculatedFontStyle.desktop}
            mobileFontStyle={calculatedFontStyle.mobile}
            globalFontFamily={globalFontFamily}
            mobileGlobalFontFamily={globalMobileFontFamily}
            fontSizeKey="textFontSize"
            mobileFontSizeKey="mobileTextFontSize"
            fontSize={calculatedFontSize.desktop}
            mobileFontSize={calculatedFontSize.mobile}
            updateFontSize={updateProp(...firstUpdateKeys, 'textFontSize')}
            updateMobileFontSize={updateProp(
              ...firstUpdateKeys,
              'mobileTextFontSize',
            )}
            lineHeightKey="lineHeight"
            mobileLineHeightKey="mobileLineHeight"
            lineHeight={calculatedLineHeight.desktop}
            mobileLineHeight={calculatedLineHeight.mobile}
            updateLineHeight={updateProp(...firstUpdateKeys, 'lineHeight')}
            updateMobileLineHeight={updateProp(
              ...firstUpdateKeys,
              'mobileLineHeight',
            )}
          />
          <GroupTitle />
          <TypographyGroup
            withoutHorizontalLine
            title="subtext typography"
            hideTextAlign
            hideLetterSpacing
            hideLineHeight
            entity={styles}
            updateEntity={updateCommonSettings}
            fontFamilyKey="subTextFontFamily"
            fontWeightKey="subTextFontWeight"
            fontStyleKey="subTextFontStyle"
            fontFileIdKey="subTextFontFileId"
            mobileFontFamilyKey="mobileSubTextFontFamily"
            mobileFontWeightKey="mobileSubTextFontWeight"
            mobileFontStyleKey="mobileSubTextFontStyle"
            mobileFontFileIdKey="mobileSubTextFontFileId"
            fontFamily={styles.subTextFontFamily}
            mobileFontFamily={styles.mobileSubTextFontFamily}
            fontWeight={styles.subTextFontWeight}
            mobileFontWeight={styles.mobileSubTextFontWeight}
            fontStyle={styles.subTextFontStyle}
            mobileFontStyle={styles.mobileSubTextFontStyle}
            globalFontFamily={globalFontFamily}
            mobileGlobalFontFamily={globalMobileFontFamily}
            fontSizeKey="subTextFontSize"
            mobileFontSizeKey="mobileSubTextFontSize"
            fontSize={styles.subTextFontSize}
            mobileFontSize={styles.mobileSubTextFontSize}
            updateFontSize={updateProp(...firstUpdateKeys, 'subTextFontSize')}
            updateMobileFontSize={updateProp(
              ...firstUpdateKeys,
              'mobileSubTextFontSize',
            )}
          />
        </>
      )}
    </>
  )
}
